import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { useGetElevesByParentQuery } from "redux_dir/features/eleveApi";
import { getCurrentEleve, setCurrentEleve } from "redux_dir/features/eleveSlice";
import { getParentForm } from "redux_dir/features/parentSlice";

/**
 * Normalise un élève (API) vers la forme stockée dans `currentEleve`.
 */
export const toCurrentEleve = (eleve) => ({
  id: eleve?.id,
  classe: eleve?.classe?.name ?? eleve?.classe,
  classeId: eleve?.classe?.id ?? eleve?.classeId,
  fullName: [eleve?.firstName, eleve?.lastName].filter(Boolean).join(" "),
});

/**
 * Liste des enfants du parent + sélection d'un enfant.
 *
 * La sélection d'un enfant ne passe plus par une page dédiée :
 * on positionne l'enfant courant et on ouvre directement sa page matière.
 */
function useEleves() {
  const dispatch = useDispatch();
  const navigation = useNavigate();

  const parent = useSelector(getParentForm);
  const currentEleve = useSelector(getCurrentEleve);

  const { data, error, isLoading, isFetching, isError, refetch } =
    useGetElevesByParentQuery(
      { parentId: parent?.id },
      { skip: !parent?.id }
    );

  const eleves = data?.data ?? [];

  /**
   * Enfant à ouvrir par défaut : le dernier consulté s'il existe encore,
   * sinon le premier de la liste.
   */
  const defaultEleve =
    eleves.find((eleve) => String(eleve?.id) === String(currentEleve?.id)) ??
    eleves[0];

  const selectEleve = useCallback(
    (eleve, { replace = false, track = null } = {}) => {
      if (!eleve?.id) return;

      dispatch(setCurrentEleve(toCurrentEleve(eleve)));

      if (track) {
        mixpanel.track(track, {
          "Enfant ID": eleve?.id,
          "Enfant Sélectionné": eleve?.firstName,
          Classe: eleve?.classe?.name ?? eleve?.classe,
        });
      }

      navigation(`/${eleve?.id}/matiere`, { replace });
    },
    [dispatch, navigation]
  );

  return {
    parent,
    eleves,
    currentEleve,
    defaultEleve,
    isLoading: isLoading || (!data && isFetching),
    isError,
    error,
    // 401 : session expirée, un nouvel essai ne servira à rien
    isUnauthorized: error?.status === 401 || error?.status === 403,
    refetch,
    selectEleve,
  };
}

export default useEleves;
